import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Joi from 'joi';
import moment from 'moment';

import {
    useGetInvestmentFundsQuery,
    useGetPromotersQuery,
    useGetRolesQuery,
    useGetUsersQuery,
    useUpdateDistributorCommercialMutation,
    useUpdateDistributorCompanyMutation,
} from 'api/main';
import { corporateForms } from 'utils/formData';
import {
    COMPANY_NAME_REGEX,
    conventionStatus,
    distributorType,
    EURO_SYMBOL,
    ORDER_BY,
    roleCategories,
    RoleNames,
    sortDirection,
} from 'utils/constants';
import {
    convertToBoolOrUndefined,
    jsonParseSafe,
    parseDate,
    parseFullName,
    parseSum,
    purgeEmptyFields,
    validateSchema,
} from 'utils/helpers';
import { userSchemas } from 'schemas';
import { useMatchAtLeastOneRole, useUserRoleMatchCategory } from 'utils/hooks';
import { texts } from 'utils/languages/fr';

import InfoRow from './InfoRow';
import InfoBox from './InfoBox';
import SectionActions from './SectionActions';
import CompanyLogo from 'containers/subscriptions/CompanyLogo';
import {
    CustomDatePicker,
    CustomSelect,
    CustomTextField,
    BasicAddressForm,
    Loader,
    RadioLabel,
    TransferList,
} from 'components';
import {
    Box,
    Checkbox,
    IconButton,
    InputAdornment,
    MenuItem,
    RadioGroup,
    Typography,
} from '@mui/material';
import * as icons from 'mdi-material-ui';

const parseOrganization = org => {
    if (typeof org !== 'boolean') return '-';
    return org ? texts.RESEAU : texts.INDEPENDANT;
};

const DistributorComplyAdvantageStatus = props => {
    const { status } = props;
    if (!status) {
        return <div>-</div>;
    }
    const info = [];
    if (status?.pep_user) {
        info.push(texts.PEP_DISTRIBUTOR_USER);
    }
    if (status?.sanction_user) {
        info.push(texts.SANCTIONS_DISTRIBUTOR_USER);
    }
    if (status?.pep_company) {
        info.push(texts.PEP_COMPANY);
    }
    if (status?.sanction_company) {
        info.push(texts.SANCTIONS_COMPANY);
    }
    if (status?.pep_legal_representative) {
        info.push(texts.PEP_DISTRIBUTOR_REPRESENTATIVE);
    }
    if (status?.sanction_legal_representative) {
        info.push(texts.SANCTIONS_DISTRIBUTOR_REPRESENTATIVE);
    }

    return (
        <div>
            {info.map(message => (
                <div key={message}>- {message}</div>
            ))}
        </div>
    );
};

DistributorComplyAdvantageStatus.propTypes = { status: PropTypes.object };

const getDefaultForm = ({ role, ...distributor }) => ({
    corporateName: distributor?.corporateName || '',
    corporateForm: jsonParseSafe(distributor?.corporateForm)?.choice || '',
    otherCorporateForm: jsonParseSafe(distributor?.corporateForm)?.other || '',
    role,
    organization: distributor?.organization || '',
    shareCapital: distributor?.shareCapital || '',
    rcsNumber: distributor?.rcsNumber || '',
    rcsRegistrationAddress: distributor?.rcsRegistrationAddress || '',
    rcsRegistrationDate: moment(distributor?.rcsRegistrationDate) || '',
    vatNumber: distributor?.vatNumber || '',
    oriasNumber: distributor?.oriasNumber || '',
    acprNumber: distributor?.acprNumber || '',
    validateSubscriptions: distributor?.validateSubscriptions || false,
    enableExternalDistributors: distributor?.enableExternalDistributors || false,
    currentLocation: {
        formattedAddress: distributor?.currentLocation?.formattedAddress,
    },
    promoters: distributor?.promoters || [],
});

const schema = {
    corporateName: Joi.string().required().pattern(COMPANY_NAME_REGEX).messages({
        'string.empty': texts.FIELD_REQUIRED,
        'string.pattern.base': texts.INVALID_INPUT,
    }),
    corporateForm: Joi.string().required().messages({ 'string.empty': texts.FIELD_REQUIRED }),
    otherCorporateForm: Joi.when('corporateForm', {
        is: Joi.string().equal(corporateForms.other),
        then: Joi.string().required().messages({ 'string.empty': texts.FIELD_REQUIRED }),
        otherwise: Joi.any(),
    }),
    shareCapital: Joi.number()
        .precision(3)
        .required()
        .messages({ 'number.base': texts.FIELD_REQUIRED }),
    rcsNumber: Joi.string().allow('').messages({ 'string.empty': texts.FIELD_REQUIRED }),
    vatNumber: Joi.string().allow('').messages({ 'string.empty': texts.FIELD_REQUIRED }),
    rcsRegistrationAddress: Joi.string()
        .trim()
        .pattern(/^[ a-zA-ZÀ-ÿ]+$/)
        .allow('')
        .messages({
            'string.empty': texts.FIELD_REQUIRED,
            'string.pattern.base': texts.RCS_REGISTRATION_ADDRESS_ONLY_LETTERS,
        }),
    rcsRegistrationDate: Joi.any()
        .invalid(null, '')
        .messages({ 'any.invalid': texts.FIELD_REQUIRED }),
    oriasNumber: Joi.string().allow('').length(8).pattern(/^\d+$/).required().messages({
        'string.empty': texts.FIELD_REQUIRED,
        'string.length': texts.ORIAS_NUMBER_LENGTH,
        'string.pattern.base': texts.ORIAS_NUMBER_LENGTH,
    }),
    currentLocation: {
        formattedAddress: Joi.string().required().messages({
            'string.empty': texts.FIELD_REQUIRED,
            'any.required': texts.FIELD_REQUIRED,
        }),
    },
    promoters: Joi.array().min(1).messages({ 'array.min': texts.AT_LEAST_ONE_PROMOTER }),
};

const CompanySection = props => {
    const { distributor, complyAdvantageStatus } = props;

    const { data: roles = [], isLoading: rolesLoader } = useGetRolesQuery();

    const subscription = distributor?.user?.subscriptions?.[0];
    const isAdminCIF = subscription?.role?.name === RoleNames.ADMIN_CIF;
    const isPSI = subscription?.role?.name === RoleNames.PSI;
    const userIsServicerAdmin = useMatchAtLeastOneRole([
        RoleNames.SERVICER_ADMIN,
        RoleNames.SERVICER_SIGNATORY,
    ]);
    const userIsPromoter = useUserRoleMatchCategory(roleCategories.PROMOTERS);
    const userIsAdminPromoter = useMatchAtLeastOneRole([RoleNames.PROMOTER_ADMIN]);

    const defaultRoleId = useMemo(
        () =>
            isPSI
                ? roles.find(role => role.name === RoleNames.PSI)?.id
                : roles.find(role => role.name === RoleNames.ADMIN_CIF)?.id,
        [isPSI, roles]
    );

    const [form, setForm] = useState(getDefaultForm({ role: defaultRoleId }));
    const [errors, setErrors] = useState({});
    const [editing, setEditing] = useState(false);

    const [promoterCommercial, setPromoterCommercial] = useState('');

    const { data: investmentFunds = [] } = useGetInvestmentFundsQuery();
    const { data: { promoters } = { promoters: [] }, isLoading: promotersLoading } =
        useGetPromotersQuery({ pagination: false }, { skip: !userIsServicerAdmin });
    const [updateDistributorCompany, { isLoading: loading, isSuccess: updateSuccessful }] =
        useUpdateDistributorCompanyMutation();
    const [updateDistributorCommercial, { isLoading: commercialUpdating }] =
        useUpdateDistributorCommercialMutation();
    const { data: { userList: promoterUsers = [] } = { userList: [] }, isFetching: listLoader } =
        useGetUsersQuery(
            userSchemas.getUsers({
                disablePagination: true,
                orderBy: ORDER_BY.lastName,
                direction: sortDirection.ASC,
                role: roles
                    .filter(r => r.category?.name === roleCategories.PROMOTERS)
                    .map(r => r.id),
            }),
            { skip: !(roles.length && userIsPromoter) }
        );

    const productsWithConventions = useMemo(
        () =>
            investmentFunds.filter(
                p =>
                    distributor.conventions.find(c => c.investmentFund.id === p.id)?.status ===
                    conventionStatus.FULLY_SIGNED
            ),
        [distributor, investmentFunds]
    );

    // promoters that are associated with funds that the distributor has signed a convention for
    const permanentPromoters = useMemo(
        () =>
            distributor?.promoters?.filter(p =>
                productsWithConventions.find(pc => pc.promoter.id === p.id)
            ),
        [distributor, productsWithConventions]
    );

    const currentCorporateForm = useMemo(() => {
        const corporateForm = jsonParseSafe(distributor?.corporateForm);

        if (corporateForm?.other) {
            return `${corporateForm.choice} - ${corporateForm.other}`;
        } else {
            return corporateForm?.choice;
        }
    }, [distributor?.corporateForm]);

    const handleChange = useCallback(
        key => e => {
            setForm(prevState => ({ ...prevState, [key]: e.target.value }));
        },
        []
    );

    const handleEdit = useCallback(() => {
        setForm(getDefaultForm({ ...distributor, role: defaultRoleId }));
        setEditing(true);
    }, [distributor, defaultRoleId]);

    const handleAddressChange = useCallback(fullAddress => {
        setForm(prevState => ({ ...prevState, currentLocation: fullAddress }));
    }, []);

    const handleDateChange = useCallback(
        newDate => setForm(prevState => ({ ...prevState, rcsRegistrationDate: newDate })),
        []
    );

    const handleChangePromoters = useCallback(newValue => {
        setForm(prevState => ({ ...prevState, promoters: newValue }));
    }, []);

    const handleSavePromoterCommercial = useCallback(() => {
        updateDistributorCommercial({
            id: distributor.id,
            promoterCommercial,
        });
    }, [updateDistributorCommercial, distributor, promoterCommercial]);

    const handleCheckboxChange = useCallback(
        key => e => {
            setForm(prevState => ({ ...prevState, [key]: e.target.checked }));
        },
        []
    );

    const handleCancel = useCallback(() => {
        setEditing(false);
        setForm(getDefaultForm({ ...distributor, role: defaultRoleId }));
    }, [distributor, defaultRoleId]);

    const handleSubmit = useCallback(() => {
        const { valid, newErrors } = validateSchema(schema, form);
        if (valid) {
            setErrors({});
            const payload = {
                id: distributor.id,
                ...purgeEmptyFields(form),
                user: { subscriptions: [{ role: `/api/roles/${form.role}` }] },
                shareCapital: +form.shareCapital || undefined,
                organization: convertToBoolOrUndefined(form.organization),
                promoters: form.promoters.map(p => `/api/promoters/${p.id}`),
                corporateForm: form.corporateForm
                    ? JSON.stringify({
                          choice: form.corporateForm,
                          ...(form.corporateForm === corporateForms.other && {
                              other: form.otherCorporateForm,
                          }),
                      })
                    : undefined,
            };
            updateDistributorCompany({
                payload,
                callback: () => {
                    setEditing(false);
                    setForm(
                        getDefaultForm({
                            ...distributor,
                            role: distributor.user.subscriptions[0].role.id,
                        })
                    );
                },
            });
        } else {
            setErrors(newErrors);
        }
    }, [form, updateDistributorCompany, distributor]);

    useEffect(() => {
        if (updateSuccessful) {
            setEditing(false);
        }
    }, [updateSuccessful]);

    useEffect(() => {
        if (userIsPromoter) {
            const availableIds = promoterUsers.map(p => p.id);
            const promoterCommercialObject =
                distributor?.managedByUsers.find(m => availableIds.includes(m.id)) ?? null;
            if (promoterCommercialObject?.id) {
                setPromoterCommercial(promoterCommercialObject.id);
            }
        }
    }, [distributor?.managedByUsers, userIsPromoter, promoterUsers]);

    useEffect(() => {
        setForm(
            getDefaultForm({
                ...distributor,
                role: distributor.user.subscriptions[0].role.id,
            })
        );
    }, [distributor]);

    return (
        <InfoBox
            leftColumnContent={
                <CompanyLogo
                    key={distributor?.companyLogo?.name}
                    id={distributor?.companyLogo?.id}
                    url={distributor?.companyLogo?.url}
                    width="100%"
                />
            }
            title={texts.GENERAL_INFO}
            titleAction={
                <IconButton onClick={editing ? null : handleEdit} disableRipple>
                    <icons.Pencil
                        sx={{ color: 'primary.main', '&:hover': { color: 'primary.dark' } }}
                    />
                </IconButton>
            }
        >
            <InfoRow
                title={texts.DISTRIBUTOR_COMPANY_NAME}
                editing={editing}
                data={distributor?.corporateName || '-'}
            >
                {editing && (
                    <CustomTextField
                        value={form.corporateName}
                        onChange={handleChange('corporateName')}
                        error={errors.corporateName}
                        formControlProps={{ sx: { mb: 0 } }}
                    />
                )}
            </InfoRow>
            {userIsPromoter ? (
                <InfoRow
                    title={texts.DISTRIBUTOR_SERVICER}
                    editing={editing}
                    data={
                        <Loader loading={rolesLoader || listLoader} marginTop={1}>
                            <Box display="flex" alignItems="center">
                                <CustomSelect
                                    fullWidth
                                    controlSx={{ marginBottom: 0 }}
                                    value={promoterCommercial}
                                    disabled={!userIsAdminPromoter}
                                    onChange={e => setPromoterCommercial(e.target.value)}
                                >
                                    {promoterUsers.map(user => (
                                        <MenuItem key={user.id} value={user.id}>
                                            {parseFullName(user)}
                                        </MenuItem>
                                    ))}
                                </CustomSelect>
                                {userIsAdminPromoter && (
                                    <IconButton
                                        disableRipple
                                        color="primary"
                                        sx={{
                                            color: 'grey.500',
                                            '&:hover': { color: 'primary.main' },
                                        }}
                                        onClick={handleSavePromoterCommercial}
                                        disabled={commercialUpdating || !promoterCommercial}
                                    >
                                        <icons.ContentSave />
                                    </IconButton>
                                )}
                            </Box>
                        </Loader>
                    }
                />
            ) : (
                <InfoRow
                    mb={2}
                    title={texts.DISTRIBUTOR_SERVICER}
                    data={distributor?.managedByUsers?.map(u => parseFullName(u)).join(', ')}
                />
            )}
            <InfoRow
                title={texts.TYPE}
                data={
                    Object.keys(distributorType).find(
                        k => distributorType[k] === distributor?.type
                    ) || '-'
                }
            />
            <InfoRow
                title={texts.NETWORKS}
                data={distributor?.networks?.map(network => network.name).join(', ') || '-'}
            />
            <InfoRow editing={editing} title="CIF / PSI" data={isPSI ? 'PSI' : 'CIF'}>
                <CustomSelect value={form.role} onChange={handleChange('role')}>
                    <MenuItem key="CIF" value={roles.find(r => r.name === RoleNames.ADMIN_CIF)?.id}>
                        CIF
                    </MenuItem>
                    <MenuItem key="PSI" value={roles.find(r => r.name === RoleNames.PSI)?.id}>
                        PSI
                    </MenuItem>
                </CustomSelect>
            </InfoRow>
            {userIsServicerAdmin && (
                <InfoRow
                    title={texts.VERIFICATION}
                    data={
                        <Checkbox
                            checked={form.validateSubscriptions}
                            tabIndex={-1}
                            disabled={!editing}
                            onChange={handleCheckboxChange('validateSubscriptions')}
                            disableRipple
                            sx={{ padding: 0 }}
                        />
                    }
                />
            )}
            {userIsServicerAdmin && (
                <InfoRow
                    title={texts.ENABLE_EXTERNAL_DISTRIBUTORS}
                    data={
                        <Checkbox
                            checked={form.enableExternalDistributors}
                            tabIndex={-1}
                            disabled={!editing}
                            onChange={handleCheckboxChange('enableExternalDistributors')}
                            disableRipple
                            sx={{ padding: 0 }}
                        />
                    }
                />
            )}
            <InfoRow
                title={texts.FORME_SOCIALE}
                editing={editing}
                data={currentCorporateForm || '-'}
            >
                {editing && (
                    <CustomSelect
                        value={form.corporateForm}
                        onChange={handleChange('corporateForm')}
                        error={errors.corporateForm}
                    >
                        {Object.values(corporateForms).map(corporateForm => (
                            <MenuItem key={corporateForm} value={corporateForm}>
                                {corporateForm}
                            </MenuItem>
                        ))}
                    </CustomSelect>
                )}
                {form.corporateForm === corporateForms.other && (
                    <CustomTextField
                        showError
                        value={form.otherCorporateForm}
                        onChange={handleChange('otherCorporateForm')}
                        error={errors.otherCorporateForm}
                    />
                )}
            </InfoRow>
            <InfoRow title={texts.ORGANISATION} data={parseOrganization(distributor?.organization)}>
                {editing && (
                    <RadioGroup value={form.organization} onChange={handleChange('organization')}>
                        <RadioLabel value="true" label={texts.RESEAU} />
                        <RadioLabel value="false" label={texts.INDEPENDANT} />
                    </RadioGroup>
                )}
            </InfoRow>
            {!!subscription?.finishedSubscriptionAt && (
                <InfoRow
                    title={texts.DATE_ENTREE}
                    data={parseDate(subscription?.finishedSubscriptionAt) || '-'}
                />
            )}
            <InfoRow
                title={texts.CAPITAL_SOCIAL}
                editing={editing}
                data={distributor?.shareCapital ? parseSum(distributor?.shareCapital) : '-'}
            >
                {editing && (
                    <CustomTextField
                        value={form.shareCapital}
                        onChange={handleChange('shareCapital')}
                        error={errors.shareCapital}
                        endAdornment={
                            <InputAdornment position="start">{EURO_SYMBOL}</InputAdornment>
                        }
                        formatNumber
                    />
                )}
            </InfoRow>
            <InfoRow editing={editing} title="SIREN / RCS" data={distributor?.rcsNumber || '-'}>
                {editing && (
                    <CustomTextField
                        value={form.rcsNumber}
                        error={errors.rcsNumber}
                        onChange={handleChange('rcsNumber')}
                    />
                )}
            </InfoRow>
            <InfoRow
                title={texts.RCS_ADDRESS}
                editing={editing}
                data={distributor?.rcsRegistrationAddress || '-'}
            >
                {editing && (
                    <CustomTextField
                        value={form.rcsRegistrationAddress}
                        onChange={handleChange('rcsRegistrationAddress')}
                        error={errors.rcsRegistrationAddress}
                    />
                )}
            </InfoRow>
            <InfoRow
                title={texts.RCS_DATE}
                editing={editing}
                data={parseDate(distributor?.rcsRegistrationDate) || '-'}
            >
                {editing && (
                    <CustomDatePicker
                        disableFuture
                        showError
                        sx={{ backgroundColor: 'white' }}
                        value={form.rcsRegistrationDate}
                        error={errors.rcsRegistrationDate}
                        onChange={handleDateChange}
                    />
                )}
            </InfoRow>
            <InfoRow
                title={texts.VAT_NUMBER}
                editing={editing}
                data={distributor?.vatNumber || '-'}
            >
                {editing && (
                    <CustomTextField
                        value={form.vatNumber}
                        onChange={handleChange('vatNumber')}
                        error={errors.vatNumber}
                    />
                )}
            </InfoRow>
            {isAdminCIF && (
                <InfoRow
                    title={texts.ORIAS_NUMBER}
                    editing={editing}
                    data={distributor?.oriasNumber || '-'}
                >
                    {editing && (
                        <CustomTextField
                            type="number"
                            value={form.oriasNumber}
                            onChange={handleChange('oriasNumber')}
                            error={errors.oriasNumber}
                            showError={!!errors.oriasNumber}
                        />
                    )}
                </InfoRow>
            )}
            {isPSI && (
                <InfoRow
                    title={texts.ACPR_NUMBER}
                    editing={editing}
                    data={distributor?.acprNumber || '-'}
                >
                    {editing && (
                        <CustomTextField
                            value={form.acprNumber}
                            error={errors.acprNumber}
                            onChange={handleChange('acprNumber')}
                        />
                    )}
                </InfoRow>
            )}
            <InfoRow
                title={texts.SIEGE_SOCIAL}
                editing={editing}
                data={distributor?.currentLocation?.formattedAddress || '-'}
            >
                {editing && (
                    <BasicAddressForm
                        useFullAddress
                        address={form.currentLocation.formattedAddress}
                        onAddressChange={handleAddressChange}
                        errors={errors['currentLocation,formattedAddress']}
                    />
                )}
            </InfoRow>
            <InfoRow
                title={texts.COMPLY_ADVANTAGE_STATUS}
                data={<DistributorComplyAdvantageStatus status={complyAdvantageStatus} />}
            />

            {userIsServicerAdmin && editing ? (
                <React.Fragment>
                    <TransferList
                        permanentItems={permanentPromoters}
                        items={promoters}
                        value={form.promoters}
                        label={
                            <Typography fontWeight="600" component="span">
                                {texts.PROMOTER}
                                {'\xa0'}:
                            </Typography>
                        }
                        textKey="name"
                        onChange={handleChangePromoters}
                        loading={promotersLoading || loading}
                        marginY={0}
                        error={errors.promoters}
                    />
                </React.Fragment>
            ) : (
                <InfoRow
                    title={texts.PROMOTERS}
                    data={distributor?.promoters?.map(p => p.name).join(', ')}
                />
            )}
            {editing && (
                <SectionActions
                    loading={loading}
                    handleCancel={handleCancel}
                    handleSubmit={handleSubmit}
                />
            )}
        </InfoBox>
    );
};

CompanySection.propTypes = {
    distributor: PropTypes.object,
    canEditMissingInfo: PropTypes.bool,
    complyAdvantageStatus: PropTypes.object,
};

export default CompanySection;
